/**
 * Pure helpers for the organization pages: the "Transações Recentes" surface on
 * `/organizacao/pagamentos`, and the phone shown on `/organizacao/info`.
 *
 * --- Transações Recentes ---
 *
 * `GET /payment/transactions` builds each row from the invoice itself and one
 * of two financial projections. Card invoices may carry a
 * `stripe_audit_balance`, the only source of their `balanceId`, card identity
 * and amount. Weekly Pix invoices instead carry `statementLines`: committed
 * services with the weekly-limit snapshot that admitted each one, an optional
 * negative customer credit and the separate processing-fee line, whose signed
 * sum is the weekly debit. They deliberately have no Stripe balance, so their
 * legacy `amount` field remains zero and must not replace that statement total.
 *
 * The frontend cannot conjure the missing ledger. What it MUST NOT do is fail
 * silently about it, which is what it did: an empty `cardLabel` rendered as an
 * empty chip, and the NFE button called `setSelectedBalanceId("")`, whose modal
 * early-returns — a click that produced no request, no float and no message.
 *
 * These helpers turn "the ledger is absent" into an explicit, testable outcome
 * so the UI can say so out loud. They deliberately invent no value.
 */

/**
 * Shown instead of an empty card chip.
 *
 * The row is real and its status is real; only the card identity is missing.
 * Saying that is honest — an empty chip reads as a rendering glitch.
 */
export const CARD_UNIDENTIFIED_LABEL = "Cartão não identificado";

/**
 * Shown when the NFE viewer is asked for a transaction with no balance id.
 *
 * There is no NFE to fetch: `nfe_audit` rows are written by the same Stripe
 * `invoice.paid` webhook path that writes `stripe_audit_balance`, so a row
 * without a balance id has no notes either.
 */
export const NFE_UNAVAILABLE_MESSAGE =
  "Nota fiscal indisponível: esta transação ainda não tem lançamento financeiro conciliado.";

/**
 * Shown instead of {@link NFE_UNAVAILABLE_MESSAGE} on a forgiven weekly charge.
 *
 * Payments (ADR 0047) never collects it and no receipt, settlement or fiscal
 * obligation exists for it, so "ainda não" would announce an entry, and a
 * charge, that will never come.
 */
export const NFE_WRITTEN_OFF_MESSAGE =
  "Nota fiscal indisponível: esta cobrança foi perdoada e não terá lançamento financeiro.";

/** Accessible name for the NFE button when the notes CAN be fetched. */
export const NFE_AVAILABLE_ACTION_LABEL = "Ver notas fiscais desta transação";

/**
 * What clicking a row's NFE button should do.
 *
 * `open` carries the balance id the modal needs; `unavailable` carries the
 * message to show the user. The point of modelling this as a value rather than
 * an `if` buried in a JSX handler is that the "no ledger" branch is then
 * impossible to silently drop — it has to be rendered.
 */
export type NFEIntent =
  | { kind: "open"; balanceId: string }
  | { kind: "unavailable"; message: string };

/**
 * Resolve the NFE intent for a transaction row.
 *
 * Any falsy or whitespace-only balance id means the transaction has no
 * reconciled financial entry, hence no notes to show. A forgiven weekly
 * charge says it will never have one.
 */
export function resolveNFEIntent(tx: {
  balanceId?: string | null;
  invoiceStatus?: string;
  weeklyBilling?: { state: string };
}): NFEIntent {
  const balanceId = (tx.balanceId ?? "").trim();
  if (!balanceId)
    return {
      kind: "unavailable",
      message: isWrittenOffWeek(tx) ? NFE_WRITTEN_OFF_MESSAGE : NFE_UNAVAILABLE_MESSAGE,
    };
  return { kind: "open", balanceId };
}

/**
 * The label to render in a transaction's card chip.
 *
 * Falls back to {@link CARD_UNIDENTIFIED_LABEL} when the backend sent nothing —
 * it does NOT guess a brand, a number or an owner.
 */
export function displayCardLabel(cardLabel?: string | null): string {
  const label = (cardLabel ?? "").trim();
  return label === "" ? CARD_UNIDENTIFIED_LABEL : label;
}

/**
 * Whether a transaction's card identity came back from the ledger.
 *
 * Kept separate from {@link displayCardLabel} so the UI can also style/annotate
 * the fallback rather than only substituting its text.
 */
export function hasIdentifiedCard(cardLabel?: string | null): boolean {
  return (cardLabel ?? "").trim() !== "";
}

export type WeeklyStatementLimit = { mode: "fixed"; cents: number } | { mode: "unlimited" };

type WeeklyStatementLineBase = {
  lineId: string;
  kind: string;
  amountCents: number;
};

/** One owner-authored line of a weekly Pix invoice statement. */
export type WeeklyStatementLine =
  | (WeeklyStatementLineBase & {
      lineClass: "service";
      /** Policy snapshot Payments froze for this particular admitted service. */
      weeklyLimit: WeeklyStatementLimit;
      weeklyLimitVersion: number;
      serviceDate?: string;
      monthlyBaseCents?: number;
      proration?: string;
      occurredAt?: string;
      negotiationInvoiceId?: string;
      cycleId?: string;
    })
  | (WeeklyStatementLineBase & {
      lineClass: "customer_credit" | "processing_fee";
    })
  | (WeeklyStatementLineBase & {
      /**
       * The fixed weekly top-up of the Saldo de Negociações, carried as its
       * own positive line of the same Pix Automático charge (kind
       * `negotiation-topup`). It is a deposit into the prepaid balance, never
       * a service, so it stays out of the service subtotal and its limits.
       */
      lineClass: "negotiation_topup";
    });

/**
 * Parsed weekly statement used by the transaction UI.
 *
 * `totalAmountCents` is the signed sum Payments froze for the invoice. Each
 * service retains its own weekly-limit snapshot because the policy may change
 * during a week. The processing fee and customer credit stay separate so the
 * UI cannot relabel either as a service. A service-only statement can
 * legitimately have neither optional line.
 */
export type WeeklyStatement = {
  lines: WeeklyStatementLine[];
  serviceAmountCents: number;
  customerCreditAmountCents?: number;
  processingFeeAmountCents?: number;
  /** The Saldo de Negociações top-up line of this charge, when it carries one. */
  negotiationTopupAmountCents?: number;
  totalAmountCents: number;
};

export type WeeklyPeriod = { startsOn: string; endsOn: string; dueOn: string };
export type WeeklyCollection = {
  phase: string;
  commandState: string;
  providerStatus?: string;
  observedAt?: string;
};

export function isCivilDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function displayCivilDate(value: string): string {
  return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`;
}

export function displayWeeklyPeriod(period: Pick<WeeklyPeriod, "startsOn" | "endsOn">): string {
  const last = new Date(`${period.endsOn}T00:00:00Z`);
  last.setUTCDate(last.getUTCDate() - 1);
  return `${displayCivilDate(period.startsOn)} a ${displayCivilDate(last.toISOString().slice(0, 10))}`;
}

/** Dates are civil projections from Payments; dueOn never proves a bank schedule. */
export function parseWeeklyPeriod(value: unknown): WeeklyPeriod | undefined | null {
  if (value === undefined) return undefined;
  if (!isRecord(value)) return null;
  const { startsOn, endsOn, dueOn } = value;
  if (
    !isCivilDate(startsOn) ||
    !isCivilDate(endsOn) ||
    !isCivilDate(dueOn) ||
    startsOn >= endsOn ||
    dueOn < endsOn
  )
    return null;
  return { startsOn, endsOn, dueOn };
}

const observedTimestamp = (value: unknown): value is string =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}T/.test(value) &&
  Number.isFinite(Date.parse(value));
const attributionString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "" && value.length <= 200;

export function parseWeeklyCollection(value: unknown): WeeklyCollection | undefined | null {
  if (value === undefined) return undefined;
  if (!isRecord(value) || !attributionString(value.phase) || !attributionString(value.commandState))
    return null;
  const parsed: WeeklyCollection = { phase: value.phase, commandState: value.commandState };
  if (value.providerStatus !== undefined) {
    if (!attributionString(value.providerStatus)) return null;
    parsed.providerStatus = value.providerStatus;
  }
  if (value.observedAt !== undefined) {
    if (!observedTimestamp(value.observedAt)) return null;
    parsed.observedAt = value.observedAt;
  }
  return parsed;
}

/**
 * Invoice status of a weekly charge the owner forgave after Efí never created
 * it (Payments ADR 0047). Payments writes it together with the week's
 * `written_off` billing state, a `stopped` collection and a `blocked` command,
 * and never collects the charge later.
 */
export const WEEKLY_WRITTEN_OFF_INVOICE_STATUS = "weekly-written-off";
export const WEEKLY_WRITTEN_OFF_BILLING_STATE = "written_off";
export const WEEKLY_WRITTEN_OFF_LABEL = "Perdoada";

/**
 * Whether Payments reports the week as forgiven. Either signal suffices: the
 * invoice status and the week's billing state are written in one transaction,
 * and a forgiven week must never read as payable, pending or forecast.
 */
export function isWrittenOffWeek(tx: {
  invoiceStatus?: string;
  weeklyBilling?: { state: string };
}): boolean {
  return (
    tx.invoiceStatus === WEEKLY_WRITTEN_OFF_INVOICE_STATUS ||
    tx.weeklyBilling?.state === WEEKLY_WRITTEN_OFF_BILLING_STATE
  );
}

/** Definitive invoice evidence prevails over an old pending provider command. */
export function hasFinalInvoiceStatus(invoiceStatus?: string): boolean {
  return [
    "paid",
    "compensated",
    "failed",
    "dispute-lost",
    WEEKLY_WRITTEN_OFF_INVOICE_STATUS,
  ].includes(invoiceStatus ?? "");
}

export function displayTransactionStatus(tx: {
  invoiceStatus: string;
  weeklyCollection?: WeeklyCollection;
  weeklyBilling?: { disposition: string; state: string };
}): string {
  const invoiceLabels: Record<string, string> = {
    paid: "Pago",
    failed: "Falhou",
    "dispute-lost": "Chargeback",
    compensated: "Compensado",
    [WEEKLY_WRITTEN_OFF_INVOICE_STATUS]: WEEKLY_WRITTEN_OFF_LABEL,
  };
  if (Object.prototype.hasOwnProperty.call(invoiceLabels, tx.invoiceStatus))
    return invoiceLabels[tx.invoiceStatus];
  // Before the contribution, period and collection readings: a forgiven week
  // is final even beside a stopped charge or a stale pending command.
  if (isWrittenOffWeek(tx)) return WEEKLY_WRITTEN_OFF_LABEL;
  if (tx.weeklyBilling?.disposition === "contribution") return "Acumulado em outra semana";
  if (tx.invoiceStatus === "weekly-open") return "Em formação";
  const billingLabels: Record<string, string> = {
    deferred: "Aguardando acúmulo",
    awaiting_preparation: "Aguardando preparação",
    held: "Preparação pendente",
    carried: "Acumulado em outra semana",
  };
  if (
    tx.weeklyBilling &&
    Object.prototype.hasOwnProperty.call(billingLabels, tx.weeklyBilling.state)
  )
    return billingLabels[tx.weeklyBilling.state];
  if (tx.weeklyCollection) {
    const { phase, commandState } = tx.weeklyCollection;
    const observedPhases: Record<string, string> = {
      cancel_requested: "Cancelamento solicitado",
      stopped: "Cobrança interrompida",
      recovery: "Em recuperação",
      awaiting_receipt: "Aguardando comprovante",
      receipt_observed: "Comprovante recebido",
    };
    if (Object.prototype.hasOwnProperty.call(observedPhases, phase)) return observedPhases[phase];
    if (phase === "pending" || commandState === "unknown") return "Aguardando confirmação";
    if (phase === "scheduled") return "Cobrança registrada";
    return "Desconhecido";
  }
  if (tx.invoiceStatus === "weekly-closed") return "Semana fechada";
  if (tx.invoiceStatus === "weekly-recovery") return "Em recuperação";
  return "Desconhecido";
}

/**
 * The style of a transaction's status chip, chosen by its invoice status.
 *
 * It reads a forgiven week like {@link displayTransactionStatus} does: a final
 * invoice status first, then {@link isWrittenOffWeek}, so the chip and its
 * Perdoada label never disagree. A status without an entry keeps the unstyled
 * `transactionStatusUnknown`; an inherited key such as `constructor` is not an
 * entry.
 */
export function transactionStatusChipClass(tx: {
  invoiceStatus: string;
  weeklyBilling?: { state: string };
}): string {
  const invoiceStatus =
    !hasFinalInvoiceStatus(tx.invoiceStatus) && isWrittenOffWeek(tx)
      ? WEEKLY_WRITTEN_OFF_INVOICE_STATUS
      : tx.invoiceStatus;
  const classByStatus: Record<string, string> = {
    paid: "transactionStatusPaid",
    failed: "transactionStatusFailed",
    "dispute-lost": "transactionStatusChargeback",
    compensated: "transactionStatusCompensated",
    "weekly-open": "transactionStatusClosed",
    "weekly-closed": "transactionStatusClosed",
    "weekly-recovery": "transactionStatusRecovery",
    // Final and neutral: forgiven is neither paid nor owed, so it shares no
    // colour with a compensation or an open, closed or pending week.
    [WEEKLY_WRITTEN_OFF_INVOICE_STATUS]: "transactionStatusWrittenOff",
  };
  return Object.prototype.hasOwnProperty.call(classByStatus, invoiceStatus)
    ? classByStatus[invoiceStatus]
    : "transactionStatusUnknown";
}

type ServiceAttribution = Pick<
  Extract<WeeklyStatementLine, { lineClass: "service" }>,
  | "serviceDate"
  | "monthlyBaseCents"
  | "proration"
  | "occurredAt"
  | "negotiationInvoiceId"
  | "cycleId"
>;
function serviceAttribution(candidate: Record<string, unknown>): ServiceAttribution | null {
  const parsed: ServiceAttribution = {};
  if (candidate.serviceDate !== undefined) {
    if (!isCivilDate(candidate.serviceDate)) return null;
    parsed.serviceDate = candidate.serviceDate;
  }
  if (candidate.monthlyBaseCents !== undefined) {
    if (
      typeof candidate.monthlyBaseCents !== "number" ||
      !Number.isSafeInteger(candidate.monthlyBaseCents) ||
      candidate.monthlyBaseCents < 0
    )
      return null;
    parsed.monthlyBaseCents = candidate.monthlyBaseCents;
  }
  for (const field of ["proration", "negotiationInvoiceId", "cycleId"] as const) {
    const value = candidate[field];
    if (value !== undefined) {
      if (!attributionString(value)) return null;
      parsed[field] = value;
    }
  }
  if (candidate.occurredAt !== undefined) {
    if (!observedTimestamp(candidate.occurredAt)) return null;
    parsed.occurredAt = candidate.occurredAt;
  }
  return parsed;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const addCents = (current: number, amount: number): number | null => {
  const total = current + amount;
  return Number.isSafeInteger(total) ? total : null;
};

/** Mirrors the exact fixed-amount bound of Payments' statement contract. */
const MAX_PAYMENTS_WEEKLY_LIMIT_CENTS = 999_999_999_999;

function parseStatementWeeklyLimit(value: unknown): WeeklyStatementLimit | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (
    value.mode === "fixed" &&
    keys.length === 2 &&
    keys.includes("mode") &&
    keys.includes("cents") &&
    typeof value.cents === "number" &&
    Number.isSafeInteger(value.cents) &&
    value.cents > 0 &&
    value.cents <= MAX_PAYMENTS_WEEKLY_LIMIT_CENTS
  ) {
    return { mode: "fixed", cents: value.cents };
  }
  if (value.mode === "unlimited" && keys.length === 1 && keys[0] === "mode") {
    return { mode: "unlimited" };
  }
  return null;
}

/**
 * Read Payments' weekly statement without guessing financial values.
 *
 * `undefined` means the field was absent, which is the normal shape of card and
 * negotiation invoices. `null` means Payments sent the field in a shape this
 * client cannot account for; callers must fail the transaction list rather
 * than silently show the legacy zero amount.
 */
export function parseWeeklyStatement(
  value: unknown,
  collectionPolicyVersion: 1 | 2 = 1,
): WeeklyStatement | undefined | null {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length === 0) return null;

  const lineIds = new Set<string>();
  const lines: WeeklyStatementLine[] = [];
  let serviceAmountCents = 0;
  let customerCreditAmountCents: number | undefined;
  let processingFeeAmountCents: number | undefined;
  let negotiationTopupAmountCents: number | undefined;
  let totalAmountCents = 0;

  for (const candidate of value) {
    if (!isRecord(candidate)) return null;
    const { lineId, lineClass, kind, amountCents } = candidate;
    if (
      typeof lineId !== "string" ||
      typeof kind !== "string" ||
      typeof amountCents !== "number" ||
      !Number.isSafeInteger(amountCents)
    ) {
      return null;
    }
    const normalizedLineId = lineId.trim();
    const normalizedKind = kind.trim();
    if (normalizedLineId === "" || lineIds.has(normalizedLineId) || normalizedKind === "")
      return null;

    let parsedLine: WeeklyStatementLine;
    if (lineClass === "service") {
      if (amountCents < 0 || normalizedKind === "pix-fee" || normalizedKind === "base-credit")
        return null;
      const weeklyLimit = parseStatementWeeklyLimit(candidate.weeklyLimit);
      if (
        weeklyLimit === null ||
        typeof candidate.weeklyLimitVersion !== "number" ||
        !Number.isSafeInteger(candidate.weeklyLimitVersion) ||
        candidate.weeklyLimitVersion <= 0
      )
        return null;
      const attribution = serviceAttribution(candidate);
      if (attribution === null) return null;
      const nextServiceTotal = addCents(serviceAmountCents, amountCents);
      if (nextServiceTotal === null) return null;
      serviceAmountCents = nextServiceTotal;
      parsedLine = {
        lineId: normalizedLineId,
        lineClass,
        kind: normalizedKind,
        amountCents,
        weeklyLimit,
        weeklyLimitVersion: candidate.weeklyLimitVersion,
        ...attribution,
      };
    } else if (lineClass === "customer_credit") {
      if (
        normalizedKind !== "base-credit" ||
        amountCents >= 0 ||
        (collectionPolicyVersion === 1 && customerCreditAmountCents !== undefined) ||
        "weeklyLimit" in candidate ||
        "weeklyLimitVersion" in candidate
      ) {
        return null;
      }
      const creditTotal = addCents(customerCreditAmountCents ?? 0, amountCents);
      if (creditTotal === null) return null;
      customerCreditAmountCents = creditTotal;
      parsedLine = {
        lineId: normalizedLineId,
        lineClass,
        kind: normalizedKind,
        amountCents,
      };
    } else if (lineClass === "processing_fee") {
      if (
        normalizedKind !== "pix-fee" ||
        amountCents <= 0 ||
        processingFeeAmountCents !== undefined ||
        "weeklyLimit" in candidate ||
        "weeklyLimitVersion" in candidate
      ) {
        return null;
      }
      processingFeeAmountCents = amountCents;
      parsedLine = {
        lineId: normalizedLineId,
        lineClass,
        kind: normalizedKind,
        amountCents,
      };
    } else if (lineClass === "negotiation_topup") {
      // Payments (P4): at most one positive top-up line per weekly charge.
      if (
        normalizedKind !== "negotiation-topup" ||
        amountCents <= 0 ||
        negotiationTopupAmountCents !== undefined ||
        "weeklyLimit" in candidate ||
        "weeklyLimitVersion" in candidate
      ) {
        return null;
      }
      negotiationTopupAmountCents = amountCents;
      parsedLine = {
        lineId: normalizedLineId,
        lineClass,
        kind: normalizedKind,
        amountCents,
      };
    } else {
      return null;
    }

    const nextTotal = addCents(totalAmountCents, amountCents);
    if (nextTotal === null) return null;
    totalAmountCents = nextTotal;
    lineIds.add(normalizedLineId);
    lines.push(parsedLine);
  }

  if (totalAmountCents < 0) return null;
  return {
    lines,
    serviceAmountCents,
    customerCreditAmountCents,
    processingFeeAmountCents,
    ...(negotiationTopupAmountCents !== undefined ? { negotiationTopupAmountCents } : {}),
    totalAmountCents,
  };
}

/* -------------------------------------------------------------------------- */
/* Organisation phone                                                          */
/* -------------------------------------------------------------------------- */

/**
 * `GET /get-organization` returns the phone split across three fields —
 * `phoneCountry`, `phoneArea`, `phoneNumber` — and `/organizacao/info` renders
 * the last two into `<InputTel>`, which is a DDD box followed by a number box.
 *
 * Measured 2026-08-04 on a real organisation: `phoneArea` = "41" and
 * `phoneNumber` = "+554197880145", so the row read «41 554197880145». The DDD
 * box was right and the country code was never rendered at all — what looked
 * like "area code in front of country code" was the NUMBER field carrying the
 * whole E.164 value, country and area included.
 *
 * That is stored data in the wrong field, not a display order bug. The write
 * path is the public access request: access-request.tsx:389-394 posts a `phone`
 * form field built as `${phoneCountry}${phoneArea}${phoneNumber}` ALONGSIDE the
 * separate `phoneCountry` / `phoneArea` fields, and enterprise-api's
 * create-org-with-owner puts that concatenation straight into the org's
 * `PhoneNumber` (controllers/internal_org.go:128). The org's own PATCH
 * /update-org sends only the subscriber digits (org.tsx), which is what
 * model/org.go documents (`phoneNumber example:"999999999"`), so records
 * created through the self-service edit are clean and records created through
 * an access request are not. Migrating them is NOT this function's job.
 *
 * This function only decides what to SHOW. It strips a country and/or area
 * prefix the number should never have carried, and it refuses to strip when
 * doing so would leave fewer digits than a Brazilian subscriber number has —
 * so a legitimate number that merely starts with the same two digits as its own
 * DDD survives intact.
 */

/** Shortest Brazilian subscriber number: 8 for a landline, 9 for a mobile. */
const MIN_SUBSCRIBER_DIGITS = 8;

export interface StoredOrgPhone {
  country?: string | null;
  phoneCountry?: string | number | null;
  phoneArea?: string | number | null;
  phoneNumber?: string | number | null;
}

export interface DisplayPhone {
  /** Digits for the DDD box. `""` when nothing usable was stored. */
  area: string;
  /** Digits for the number box, with any country/area prefix removed. */
  number: string;
}

const digitsOnly = (value: string | number | null | undefined): string =>
  String(value ?? "").replace(/\D/g, "");

/**
 * Drop `prefix` from the front of `value`, but only when what remains is still
 * long enough to be a subscriber number. Without that guard, an ordinary
 * "418765432" belonging to DDD 41 would be mutilated into "8765432".
 */
function stripLeading(value: string, prefix: string): string {
  if (!prefix) return value;
  if (!value.startsWith(prefix)) return value;
  if (value.length - prefix.length < MIN_SUBSCRIBER_DIGITS) return value;
  return value.slice(prefix.length);
}

/**
 * Split the three stored fields into the pair `<InputTel>` renders.
 *
 * Invents nothing: every digit it returns came from the stored record, and a
 * record that is already well-formed passes through unchanged.
 */
export function splitStoredOrgPhone(stored: StoredOrgPhone): DisplayPhone {
  const country = digitsOnly(stored.phoneCountry);
  const area = digitsOnly(stored.phoneArea);
  let rest = digitsOnly(stored.phoneNumber);

  // Access-request rows may have the full Brazilian E.164 value in the
  // number field while phoneCountry was never stored. Strip +55 only when the
  // organization is Brazilian and the number has full country+DDD length.
  const isBrazilian = /^(br|bra|brasil|brazil)$/i.test(stored.country?.trim() || "");
  const inferredCountry =
    !country && isBrazilian && (rest.length === 12 || rest.length === 13) && rest.startsWith("55")
      ? "55"
      : country;
  rest = stripLeading(rest, inferredCountry);

  if (area) {
    return { area, number: stripLeading(rest, area) };
  }

  // No DDD stored at all, but the number is still DDD-length + subscriber
  // (10 digits landline, 11 mobile). Recover the DDD rather than showing an
  // empty box next to an eleven-digit blob.
  if (rest.length === MIN_SUBSCRIBER_DIGITS + 2 || rest.length === MIN_SUBSCRIBER_DIGITS + 3) {
    return { area: rest.slice(0, 2), number: rest.slice(2) };
  }

  return { area, number: rest };
}
